import React from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from '../../components/ui/Button';
import { TONE } from '../../components/ui/tokens';
import { lineUnitSize } from '../../utils/invoiceLineMath';
import type { UnitSizeReading } from '../../utils/invoiceLineMath';

/**
 * HOW TO READ A UNIT SIZE LIKE "20ct*1.5oz" — asked on the line it belongs to.
 * Prod USC-BUG-0030 / 0031 / 0032, Sea Food Enterprises 127632-12.
 *
 * "20ct*1.5oz" was meant as twenty 1.5 oz pieces in each pack, 30 oz. Prod also
 * holds "12x28oz", where 12 is the case count and each can is 28 oz. The text
 * cannot say which, so the reviewer is asked, once (owner's decision,
 * 2026-09-15). Confirm & sync refuses until every such line is answered
 * (`UNIT_SIZE_READING_REQUIRED`).
 *
 * Whether to ask, and both readings, come from `invoiceLineMath.lineUnitSize` —
 * the mirrored twin the server's confirm gate asks too. This component only
 * says them, in the line's own numbers. The answer is staged through the grid's
 * own line-edit path and saved with everything else; retyping the Unit Size
 * clears it, and the question comes back.
 */

export interface UnitSizeReadingPromptProps {
  /** The line as the grid shows it, unsaved edits included. */
  line: any;
  lineNo: number;
  /** View mode: the question is shown, and cannot be answered from here. */
  readOnly?: boolean;
  onAnswer: (answer: UnitSizeReading) => void;
}

export function UnitSizeReadingPrompt({ line, lineNo, readOnly = false, onAnswer }: UnitSizeReadingPromptProps) {
  const reading = lineUnitSize(line);
  if (!reading.needsAnswer || !reading.countTimesSize || !reading.readings) return null;

  const typed = String(line?.splitUnitSize ?? '').trim();
  const { count } = reading.countTimesSize;
  const holdsCountTimesSize = `Each unit holds ${count} × ${reading.readings.each} = ${reading.readings.count_times_size}`;
  const countIsTheCase = `${count} is the case count — each unit is ${reading.readings.each}`;

  return (
    <div
      role="group"
      aria-label={`Line ${lineNo}: how to read Unit Size ${typed}`}
      className={`sticky left-0 flex w-fit max-w-3xl flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs ${TONE.attention}`}
    >
      <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      <span>
        Line {lineNo}: Unit Size <span className="font-semibold">{typed}</span> can be read two ways. Which is it?
      </span>
      <Button size="sm" disabled={readOnly} onClick={() => onAnswer('count_times_size')}>
        {holdsCountTimesSize}
      </Button>
      <Button size="sm" disabled={readOnly} onClick={() => onAnswer('each')}>
        {countIsTheCase}
      </Button>
    </div>
  );
}

export default UnitSizeReadingPrompt;
